import {
  KnownProjectTeamMembershipDtoAccessLevel,
  KnownProjectTeamMembershipDtoStatus,
  ProjectTeamMemberDtoAccessLevel
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { isCarrierProjectType } from '../../../../app/navigation/courier-project-type';

export const teamMembershipAccessLevelStrings: Record<KnownProjectTeamMembershipDtoAccessLevel, string> = {
  'support-basic': 'Support Basic',
  'support-full': 'Support Full',
  'groundfloor-manager': 'Groundfloor Manager',
  'groundfloor-support-full': 'Groundfloor Support Full',
  admin: 'Admin',
  deliver: 'Deliver',
  manager: 'Manager',
  self: 'Self',
  'carrier-manager': 'Courier Manager',
  'partner-integrator-batch-only': 'Partner Integrator Batch Only',
  'partner-integrator-batch-and-deliveries': 'Partner Integrator Batch and Deliveries'
};

/**
 * What each access level lets someone do. `{project}` and `{users}` are filled from the project's
 * own labels, so a store reads "your store" rather than "your building".
 *
 * Resolve through `useAccessLevelDescriptions`, which also swaps sentences that are wrong for a
 * project type beyond their nouns.
 */
export const teamMembershipAccessLevelDetailStrings: Record<ProjectTeamMemberDtoAccessLevel, string> = {
  admin: 'Access to absolutely everything, with the power to potentially break things.',
  manager: 'Manage all aspects of your {project} including deliveries, {users}, settings and portal users.',
  'support-full':
    "Support {users} of your {project}'s lockers, including opening lockers and changing the status of deliveries.",
  'support-basic': "Assist {users} of your {project}'s lockers, without having any ability to open lockers.",
  deliver: 'Deliver into the lockers on behalf of the {project}',
  'carrier-manager': 'Manage courier details including login credentials.'
};

/**
 * Descriptions whose sentence is wrong beyond its nouns, keyed by access level. A carrier has no
 * lockers, so swapping a noun cannot make the locker roles read correctly for one.
 */
export const carrierAccessLevelDetailStrings: Record<string, string> = {
  'support-full': "Support this carrier's couriers and their deliveries, without changing their sign-in details."
};

/**
 * The access levels a project offers its team. A carrier or region project has no lockers to assist
 * someone at and nobody delivering on its behalf, so those two give way to the carrier role.
 */
export function accessLevelsForProjectType(projectType?: string): KnownProjectTeamMembershipDtoAccessLevel[] {
  if (isCarrierProjectType(projectType)) {
    return [
      KnownProjectTeamMembershipDtoAccessLevel.Manager,
      KnownProjectTeamMembershipDtoAccessLevel.SupportFull,
      KnownProjectTeamMembershipDtoAccessLevel.CarrierManager
    ];
  }

  return [
    KnownProjectTeamMembershipDtoAccessLevel.Manager,
    KnownProjectTeamMembershipDtoAccessLevel.SupportFull,
    KnownProjectTeamMembershipDtoAccessLevel.SupportBasic,
    KnownProjectTeamMembershipDtoAccessLevel.Deliver
  ];
}

export const teamMemberStatusStrings: Record<KnownProjectTeamMembershipDtoStatus, string> = {
  active: 'Active',
  invited: 'Invited',
  removed: 'Removed',
  suspended: 'Suspended'
};
